// frontend/src/pages/tools/UtmBuilder.js
//
// Free UTM builder at /tools/utm-builder.
//
// Targets `utm builder` (10K-100K bucket, $11.78 bid, competition 8) — the
// largest volume-and-bid combination anywhere in the 18,463-keyword set — plus
// the variants `google utm builder`, `utm generator`, `utm code generator` and
// `campaign url builder`, which sit at competition 5-11.
//
// The head term's SERP holds Google's own Campaign URL Builder, UTM.io, Fathom
// and the exact-match domains utmbuilder.com/.net, so the variants are the
// realistic target rather than `utm builder` itself.
//
// CONVERSION DESIGN
// The tool is fully usable with no account, deliberately. Gating it would earn
// no links and no rankings, which is the entire reason to build it. Sign-up is
// attached to the two things people actually want to keep — saved presets and
// link history — and to the one genuine next step: someone who has just built a
// tracked link is about to post it somewhere.
import '../../custom.css'

import React, { useState, useMemo } from 'react'
import { Container, Row, Col, Form, Image, Button, Card, Accordion } from 'react-bootstrap'
import { Clipboard, CheckCircleFill, Lightning, ExclamationTriangleFill } from 'react-bootstrap-icons'

import Seo from '../../components/Seo'

const PARAMS = [
  ['utm_source',   'Where the traffic comes from',        'instagram',    true],
  ['utm_medium',   'What kind of channel it is',          'social',       true],
  ['utm_campaign', 'Which campaign it belongs to',        'summer_launch', true],
  ['utm_content',  'Which link, when a page has several', 'bio_link',     false],
  ['utm_term',     'Paid keyword — rarely used organically', 'running_shoes', false],
]

// Quick-fill chips. These double as a demo of the saved-presets idea: the reason
// teams need presets is that utm_source=Facebook and facebook are different
// values in Google Analytics, so inconsistent naming splits your reporting.
const PRESETS = [
  ['Instagram post',  { source: 'instagram', medium: 'social' }],
  ['Instagram bio',   { source: 'instagram', medium: 'social', content: 'bio_link' }],
  ['Facebook',        { source: 'facebook',  medium: 'social' }],
  ['LinkedIn',        { source: 'linkedin',  medium: 'social' }],
  ['TikTok',          { source: 'tiktok',    medium: 'social' }],
  ['Email newsletter',{ source: 'newsletter', medium: 'email' }],
]

const FAQS = [
  ['What is a UTM builder?',
   'A UTM builder adds tracking parameters to a URL so your analytics can tell you where visitors came from. Without them, a link shared on Instagram, in an email and on LinkedIn all look identical in your reports.'],
  ['Is this UTM builder free?',
   'Yes, completely, and no account is needed. Sign up only if you want to save presets or keep a history of the links you have built.'],
  ['What are the five UTM parameters?',
   'utm_source (where from), utm_medium (what kind of channel), utm_campaign (which campaign), utm_content (which link) and utm_term (paid keyword). The first three are the ones almost everyone uses.'],
  ['Are UTM parameters case sensitive?',
   'Yes, and this catches people out constantly. Google Analytics treats utm_source=Facebook and utm_source=facebook as two different sources, splitting one channel across two rows. Keep everything lowercase.'],
  ['Do UTM parameters affect SEO?',
   'Not meaningfully on links you share externally. Avoid using them on internal links between your own pages, where they can fragment analytics and create duplicate URLs.'],
  ['Is a UTM generator the same as a UTM builder?',
   'Yes. UTM builder, UTM generator, UTM maker, UTM creator and campaign URL builder all describe the same thing — a tool that assembles a URL with utm_ parameters attached. The parameters are a Google Analytics standard, so every one of them produces an identical link.'],
  ['What is the difference between this and Google Campaign URL Builder?',
   'The parameters are identical — it is the same standard. This one adds quick presets for social channels, warns about the casing problem, and lets you schedule the tagged link straight to your social accounts.'],
  ['Can I tag links in bulk?',
   'Saved presets make repeat tagging fast. If you are scheduling many posts at once, bulk posting takes a spreadsheet of posts and links together.'],
]

export default function UtmBuilder () {
  const [f, setF] = useState({ url: '', source: '', medium: '', campaign: '', content: '', term: '' })
  const [lower, setLower] = useState(true)
  const [copied, setCopied] = useState(false)

  const set = (k, v) => { setF(p => ({ ...p, [k]: v })); setCopied(false) }
  const applyPreset = vals => { setF(p => ({ ...p, ...vals })); setCopied(false) }

  const clean = v => (lower ? String(v).trim().toLowerCase() : String(v).trim()).replace(/\s+/g, '_')

  const result = useMemo(() => {
    const base = f.url.trim()
    if (!base) return ''
    const parts = []
    for (const [key, val] of [['utm_source', f.source], ['utm_medium', f.medium],
                              ['utm_campaign', f.campaign], ['utm_content', f.content],
                              ['utm_term', f.term]]) {
      if (val && val.trim()) parts.push(`${key}=${encodeURIComponent(clean(val))}`)
    }
    if (!parts.length) return base
    // Respect a URL that already carries a query string.
    return base + (base.includes('?') ? '&' : '?') + parts.join('&')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, lower])

  const ready = result && f.url.trim() && (f.source || f.medium || f.campaign)
  const messyCase = !lower && [f.source, f.medium, f.campaign].some(v => v && v !== v.toLowerCase())

  const copy = () => {
    if (!result) return
    navigator.clipboard?.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2200)
  }

  return (
    <>
      <Seo
        title='Free UTM Builder — Campaign URL Generator'
        description='Build UTM tracking links in seconds. Free, no sign-up, with presets for every social channel and a warning for the casing mistake that splits your analytics.'
        path='/tools/utm-builder'
        jsonLd={[{
          '@context': 'https://schema.org', '@type': 'FAQPage',
          mainEntity: FAQS.map(([q, a]) => ({
            '@type': 'Question', name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }, {
          '@context': 'https://schema.org', '@type': 'WebApplication',
          name: 'UTM Builder', applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }]}
      />

      {/* ───────── TOOL ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <div className='text-center mb-4'>
            <h1 className='display-6 fw-bold mb-2'>Free UTM Builder</h1>
            <p className='lead mb-1'>
              Build a tracking link in seconds. No sign-up, no limits.
            </p>
            <p className='text-muted small mb-0'>
              Also known as a UTM generator, UTM maker, UTM code creator or campaign URL builder
              — same tool, same parameters as Google&rsquo;s Campaign URL Builder.
            </p>
          </div>

          <Card className='shadow-card'>
            <Card.Body className='p-4'>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-semibold small'>Website URL <span className='text-danger'>*</span></Form.Label>
                <Form.Control
                  type='url' placeholder='https://greatlysocial.com/plans'
                  value={f.url} onChange={e => set('url', e.target.value)}
                />
              </Form.Group>

              <div className='mb-3'>
                <div className='fw-semibold small mb-2'>Quick fill</div>
                <div className='d-flex flex-wrap gap-2'>
                  {PRESETS.map(([label, vals]) => (
                    <Button key={label} size='sm' variant='outline-primary'
                            onClick={() => applyPreset(vals)}>{label}</Button>
                  ))}
                </div>
              </div>

              <Row className='g-3'>
                {PARAMS.map(([key, help, eg, required]) => {
                  const name = key.replace('utm_', '')
                  return (
                    <Col md={6} key={key}>
                      <Form.Label className='fw-semibold small mb-1'>
                        {key}{required && <span className='text-danger'> *</span>}
                      </Form.Label>
                      <Form.Control
                        placeholder={eg} value={f[name]}
                        onChange={e => set(name, e.target.value)}
                      />
                      <Form.Text className='text-muted' style={{ fontSize: 12 }}>{help}</Form.Text>
                    </Col>
                  )
                })}
              </Row>

              <Form.Check
                type='switch' id='lowercase' className='mt-3'
                label={<span className='small'>Force lowercase (recommended — UTM values are case sensitive)</span>}
                checked={lower} onChange={e => { setLower(e.target.checked); setCopied(false) }}
              />

              {messyCase && (
                <div className='mt-3 p-3 rounded-3 d-flex gap-2'
                     style={{ background: '#f7efdf', border: '1px solid #e8d6ab' }}>
                  <ExclamationTriangleFill className='text-warning mt-1 flex-shrink-0' />
                  <div className='small'>
                    You have capital letters in your values. Google Analytics treats{' '}
                    <code>Facebook</code> and <code>facebook</code> as two different sources,
                    which splits one channel across two rows in your reports.
                  </div>
                </div>
              )}

              <div className='mt-4 p-3 rounded-3' style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <div className='fw-semibold small mb-2'>Your tracking link</div>
                <div className='p-3 rounded-2 mb-3'
                     style={{ background: '#fff', border: '1px solid #e5e7eb', wordBreak: 'break-all',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13,
                              minHeight: 52, color: result ? '#111827' : '#9ca3af' }}>
                  {result || 'Enter a URL above and your tagged link appears here'}
                </div>

                <Button variant='primary' onClick={copy} disabled={!result} className='me-2 mb-2'>
                  {copied ? <><CheckCircleFill className='me-1' /> Copied</> : <><Clipboard className='me-1' /> Copy link</>}
                </Button>

                {/* The genuine next step: someone who has just built a tracked link
                    is about to post it somewhere. */}
                {ready && (
                  <Button variant='success' href='/register' className='mb-2'>
                    <Lightning className='me-1' /> Schedule this link to your accounts
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <p className='text-center text-muted small mt-3 mb-0'>
            Free forever, no account needed. <a href='/register'>Create a free account</a> to save
            presets and keep a history of your links.
          </p>
        </Container>
      </section>

      {/* ───────── DEFINITION (snippet target) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-3'>What are UTM parameters?</h2>
          <p className='fs-5'>
            <strong>UTM parameters</strong> are tags added to the end of a URL that tell your
            analytics where a visitor came from. Same page for the visitor — the tags do nothing
            they can see — but Google Analytics reads them and reports the traffic by source.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Without them, a link shared on Instagram, in a newsletter and on LinkedIn all look
            identical in your reports. You see 300 visits and no idea which channel earned them.
          </p>
        </Container>
      </section>

      {/* ───────── THE FIVE PARAMETERS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold mb-4'>The five parameters</h2>
          <div className='table-responsive'>
            <table className='table table-bordered bg-white align-middle'>
              <thead className='table-light'>
                <tr><th>Parameter</th><th>Answers</th><th>Example</th><th>Needed?</th></tr>
              </thead>
              <tbody>
                {PARAMS.map(([key, help, eg, required]) => (
                  <tr key={key}>
                    <td><code>{key}</code></td>
                    <td className='text-muted'>{help}</td>
                    <td className='text-muted'><code>{eg}</code></td>
                    <td className='text-muted small'>{required ? 'Usually' : 'Optional'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            The first three do almost all the work. <code>utm_content</code> earns its keep when a
            single page holds several links to the same destination — a bio link and a story swipe-up,
            for instance.
          </p>
        </Container>
      </section>

      {/* ───────── NAMING (the real value) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-3'>The mistake that ruins the data</h2>
          <p className='text-muted fs-5'>
            Building one link is easy. Keeping the naming consistent across a team is what actually
            decides whether your reports are usable.
          </p>
          <ul className='text-muted fs-5'>
            <li className='mb-2'><strong>Case matters.</strong> <code>Facebook</code> and <code>facebook</code> are two different sources in Google Analytics. Pick lowercase and never deviate.</li>
            <li className='mb-2'><strong>Pick one word per channel.</strong> If half the team writes <code>ig</code> and half writes <code>instagram</code>, one channel becomes two rows.</li>
            <li className='mb-2'><strong>Use underscores, not spaces.</strong> Spaces become <code>%20</code> and make links ugly and error-prone.</li>
            <li><strong>Never tag internal links.</strong> Tagging links between your own pages restarts sessions and corrupts attribution.</li>
          </ul>
          <div className='p-4 rounded-3 mt-4' style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
            <h3 className='h6 fw-bold mb-2'>Save your naming convention</h3>
            <p className='text-muted small mb-3'>
              A free Greatly Social account lets you save your source and medium values as presets,
              so everyone tags links the same way — and keeps a history of every link you have built.
            </p>
            <Button variant='primary' size='sm' href='/register' className='me-2'>Create a free account</Button>
            <Button variant='outline-secondary' size='sm' href='/plans'>See plans</Button>
          </div>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <Row className='align-items-center g-4'>
            <Col md={7}>
              <h2 className='fw-bold mb-3'>Tag the link, then schedule it</h2>
              <p className='text-muted fs-5'>
                Most people building a tracking link are about to post it somewhere. Greatly Social
                schedules that post to Instagram, Facebook, Threads, X, LinkedIn, TikTok, Pinterest
                and YouTube — from one calendar, with a caption tailored per network.
              </p>
              <p className='text-muted'>
                So the link you just built goes straight into a scheduled post rather than a notes
                app you will lose.
              </p>
              <Button variant='primary' href='/register' className='me-2 mb-2'>Start using Greatly Social free</Button>
              <Button variant='outline-primary' href='/calendar-view' className='mb-2'>See the calendar</Button>
              <p className='text-muted small mb-0 mt-3'>
                Also useful: <a href='/instagram-scheduler'>Instagram scheduler</a> ·{' '}
                <a href='/linkedin-scheduler'>LinkedIn scheduler</a> ·{' '}
                <a href='/link-in-bio'>link in bio</a> ·{' '}
                <a href='/bulk-posting'>bulk posting</a>
              </p>
              <p className='text-muted small mb-0 mt-2'>14-day free trial · no credit card · from $9/month</p>
            </Col>
            <Col md={5}>
              <Image src='/assets/logo/calender.webp'
                     alt='Tagged links scheduled across networks on the Greatly Social calendar'
                     fluid className='rounded-3 border mb-3 w-100' />
              <ul className='list-unstyled mb-0'>
                {['Eight networks from one calendar',
                  'A caption per network on the same post',
                  'Queue a month from one spreadsheet',
                  'Link in bio included, with click analytics'].map(t => (
                  <li key={t} className='d-flex align-items-start gap-2 mb-2'>
                    <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                    <span className='small'>{t}</span>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>UTM builder FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>
    </>
  )
}
